import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { debugLog, debugError, debugWarn } from '@/lib/debugUtils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { WorkflowType } from '../types';
import { createSecureAIWebhookPayload, validateWorkspaceOwnership } from '@/modules/app/security';
import { emergencyFilterWorkspaceData } from '@/modules/app/security/utils/contaminationDetection';
import { withAIAnalysisUsageCheck, handleUsageError } from '../../utils/usageMiddleware';

/**
 * Enhanced webhook trigger parameters for AI analysis
 */
export interface AiAnalysisWebhookTriggerParams {
  query?: string; // Made optional for workspace analysis
  language_name?: string;
  location_name?: string;
  workflow_type?: WorkflowType;
  workspace_id?: string;
  workspace_name?: string;
  related_search_id?: string;
  // New workspace analysis parameters
  analysis_type?: 'query_analysis' | 'workspace_analysis';
  include_data?: boolean;
}

/**
 * Enhanced webhook response from N8N trigger for AI analysis
 */
export interface AiAnalysisWebhookTriggerResponse {
  success: boolean;
  message?: string;
  execution_id?: string;
  workflow_id?: string;
  data?: any;
  error?: string;
}

/**
 * Internal payload sent to N8N webhook for AI analysis
 */
interface AiAnalysisWebhookPayload {
  workspace_id: string;
  workspace_name: string;
  analysis_type: string;
  data_filter: {
    data_types: string[];
    date_range?: {
      start: string;
      end: string;
    };
  };
  include_data: boolean;
  use_aggregated: boolean;
  userId: string;
  timestamp: string;
  metadata: {
    source: 'keyword_os_frontend';
    version: '1.0.0';
    user_agent: string;
    workspace_context: {
      workspace_id: string;
      workspace_name: string;
      total_keywords: number;
      data_types: string[];
      date_range: {
        oldest_keyword: string;
        newest_keyword: string;
      };
    };
  };
  keyword_data: any[]; // Direct keyword data array, not wrapped
}

/**
 * Workflow execution tracking data for AI analysis
 */
export interface AiAnalysisWorkflowExecutionRecord {
  user_id: string;
  workflow_id: string;
  execution_id: string;
  workflow_type: WorkflowType;
  status: 'running';
  input_data: Record<string, any>;
  related_search_id?: string;
  started_at: string;
}

/**
 * Enhanced error types for AI analysis
 */
export enum AiAnalysisWebhookErrorType {
  AUTHENTICATION_ERROR = 'authentication_error',
  VALIDATION_ERROR = 'validation_error',
  NETWORK_ERROR = 'network_error',
  SERVER_ERROR = 'server_error',
  TIMEOUT_ERROR = 'timeout_error',
  CONFIGURATION_ERROR = 'configuration_error',
  WORKFLOW_CREATION_ERROR = 'workflow_creation_error',
  WORKSPACE_ERROR = 'workspace_error',
  TRACKING_ERROR = 'tracking_error',
  DATA_FETCH_ERROR = 'data_fetch_error',
  SECURITY_ERROR = 'security_error'
}

export interface AiAnalysisWebhookError {
  type: AiAnalysisWebhookErrorType;
  message: string;
  details?: any;
  retryable: boolean;
}

/**
 * AI Analysis hook return interface
 */
export interface UseAiAnalysisWebhookReturn {
  /**
   * Trigger a webhook with enhanced tracking and AI analysis support
   */
  triggerWebhook: (params: AiAnalysisWebhookTriggerParams) => Promise<AiAnalysisWebhookTriggerResponse>;
  
  /**
   * Current loading state
   */
  isLoading: boolean;
  
  /**
   * Current error state with enhanced error information
   */
  error: AiAnalysisWebhookError | null;
  
  /**
   * Last successful execution ID for tracking
   */
  lastExecutionId: string | null;
  
  /**
   * Retry the last failed webhook trigger
   */
  retryLastTrigger: () => Promise<void>;
  
  /**
   * Clear current error state
   */
  clearError: () => void;
  
  /**
   * Get user-friendly error message
   */
  getErrorMessage: () => string | null;
}

/**
 * Enhanced webhook trigger hook for AI analysis with full tracking capabilities
 * 
 * Features:
 * - Stores workflow execution records in Supabase
 * - Enhanced error handling with retry capabilities
 * - Integration with workspace auto-creation flow
 * - Integration with workflow status monitoring
 * - User-friendly error messages
 * - Timeout handling and network error recovery
 * - Comprehensive logging for debugging
 * - Full execution tracking and monitoring
 * - Workspace data fetching and analysis
 * 
 * @returns Enhanced webhook trigger interface for AI analysis
 */
export function useAiAnalysisWebhook(): UseAiAnalysisWebhookReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AiAnalysisWebhookError | null>(null);
  const [lastExecutionId, setLastExecutionId] = useState<string | null>(null);
  
  const { userId, isAuthenticated, user } = useAuth();
  const userEmail = user?.email || null;
  const lastParamsRef = useRef<AiAnalysisWebhookTriggerParams | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Create enhanced error object
   */
  const createError = useCallback((
    type: AiAnalysisWebhookErrorType,
    message: string,
    details?: any,
    retryable: boolean = false
  ): AiAnalysisWebhookError => ({
    type,
    message,
    details,
    retryable
  }), []);

  /**
   * Get user-friendly error message
   */
  const getErrorMessage = useCallback((): string | null => {
    if (!error) return null;

    const errorMessages: Record<AiAnalysisWebhookErrorType, string> = {
      [AiAnalysisWebhookErrorType.AUTHENTICATION_ERROR]: 'Please log in to start AI analysis.',
      [AiAnalysisWebhookErrorType.VALIDATION_ERROR]: 'Please check your analysis parameters and try again.',
      [AiAnalysisWebhookErrorType.NETWORK_ERROR]: 'Network connection failed. Please check your internet connection and try again.',
      [AiAnalysisWebhookErrorType.SERVER_ERROR]: 'Our servers are experiencing issues. Please try again in a few moments.',
      [AiAnalysisWebhookErrorType.TIMEOUT_ERROR]: 'The analysis is taking longer than usual. You can continue using the app - we\'ll notify you when it\'s ready.',
      [AiAnalysisWebhookErrorType.CONFIGURATION_ERROR]: 'Service is temporarily unavailable. Please contact support if this persists.',
      [AiAnalysisWebhookErrorType.WORKFLOW_CREATION_ERROR]: 'Failed to start the AI analysis process. Please try again.',
      [AiAnalysisWebhookErrorType.WORKSPACE_ERROR]: 'Failed to create or access workspace. Please try again.',
      [AiAnalysisWebhookErrorType.TRACKING_ERROR]: 'Analysis started but tracking failed. The analysis will continue normally.',
      [AiAnalysisWebhookErrorType.DATA_FETCH_ERROR]: 'Failed to fetch workspace data for analysis. Please try again.',
    };

    return errorMessages[error.type] || error.message;
  }, [error]);

  /**
   * Store workflow execution record in Supabase with AI analysis filtering
   */
  const storeWorkflowExecution = useCallback(async (
    executionId: string,
    workflowId: string,
    params: AiAnalysisWebhookTriggerParams
  ): Promise<void> => {
    if (!userId) return;

    try {
      const executionRecord: AiAnalysisWorkflowExecutionRecord = {
        user_id: userId,
        workflow_id: workflowId,
        execution_id: executionId,
        workflow_type: params.workflow_type || 'ai_analysis',
        status: 'running',
        input_data: {
          query: params.query,
          language_name: params.language_name || 'English',
          location_name: params.location_name || 'United States',
          workspace_id: params.workspace_id,
          workspace_name: params.workspace_name,
          analysis_type: params.analysis_type,
        },
        related_search_id: params.related_search_id,
        started_at: new Date().toISOString(),
      };

      const { error: dbError } = await supabase
        .from('workflow_executions')
        .insert([executionRecord]);

      if (dbError) {
        debugError(userEmail, '[useAiAnalysisWebhook] Failed to store workflow execution:', dbError);
        // Don't throw here - workflow tracking is supplementary
      } else {
        debugLog(userEmail, '[useAiAnalysisWebhook] Workflow execution stored:', executionId);
      }
    } catch (err) {
      debugError(userEmail, '[useAiAnalysisWebhook] Error storing workflow execution:', err);
      // Don't throw - this shouldn't block the main workflow
    }
  }, [userId]);

  /**
   * Fetch workspace data from all keyword tables
   */
  const fetchWorkspaceData = useCallback(async (
    workspaceId: string,
    userId: string
  ): Promise<{ keywords: any[], data_types: string[], total_keywords: number, date_range: { oldest_keyword: string, newest_keyword: string } }> => {
    try {
      debugLog(userEmail, '[useAiAnalysisWebhook] Fetching workspace data for:', { workspaceId, userId });

      // Fetch data from consolidated table (which contains all data types)
      const { data, error } = await supabase
        .from('keyword_consolidated_data')
        .select('*')
        .eq('user_id', userId)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) {
        debugError(userEmail, '[useAiAnalysisWebhook] Failed to fetch workspace data:', error);
        throw createError(
          AiAnalysisWebhookErrorType.DATA_FETCH_ERROR,
          'Failed to fetch workspace data for analysis',
          { originalError: error },
          true
        );
      }

      let allKeywords = data || [];
      
      // CRITICAL: Apply emergency contamination filtering before AI processing
      allKeywords = emergencyFilterWorkspaceData(allKeywords, workspaceId, {
        logFiltering: true,
        fieldName: 'AI_analysis_webhook_data'
      });
      
      const dataTypes = [...new Set(allKeywords.map(k => k.data_type))];

      // Calculate date range
      const dates = allKeywords.map(k => new Date(k.created_at).getTime()).filter(d => !isNaN(d));
      const oldestKeyword = dates.length > 0 ? new Date(Math.min(...dates)).toISOString() : new Date().toISOString();
      const newestKeyword = dates.length > 0 ? new Date(Math.max(...dates)).toISOString() : new Date().toISOString();

      debugLog(userEmail, '[useAiAnalysisWebhook] Total workspace data fetched:', {
        total_keywords: allKeywords.length,
        data_types: dataTypes,
        tables_with_data: dataTypes.length
      });

      return {
        keywords: allKeywords,
        data_types: dataTypes,
        total_keywords: allKeywords.length,
        date_range: {
          oldest_keyword: oldestKeyword,
          newest_keyword: newestKeyword
        }
      };

    } catch (err) {
      debugError(userEmail, '[useAiAnalysisWebhook] Error fetching workspace data:', err);
      if (err.type && Object.values(AiAnalysisWebhookErrorType).includes(err.type)) {
        throw err;
      }
      throw createError(
        AiAnalysisWebhookErrorType.DATA_FETCH_ERROR,
        'Failed to fetch workspace data for analysis',
        { originalError: err },
        true
      );
    }
  }, [createError]);

  /**
   * Validate webhook parameters with enhanced AI analysis validation
   */
  const validateParams = useCallback((params: AiAnalysisWebhookTriggerParams): void => {
    // For workspace analysis, workspace_id is required instead of query
    if (params.analysis_type === 'workspace_analysis') {
      if (!params.workspace_id?.trim()) {
        throw createError(
          AiAnalysisWebhookErrorType.VALIDATION_ERROR,
          'Workspace ID is required for workspace analysis',
          { field: 'workspace_id' },
          false
        );
      }
    } else {
      // For query analysis, query is required
      if (!params.query?.trim()) {
        throw createError(
          AiAnalysisWebhookErrorType.VALIDATION_ERROR,
          'Analysis query is required for query-based analysis',
          { field: 'query' },
          false
        );
      }

      if (params.query.length > 500) {
        throw createError(
          AiAnalysisWebhookErrorType.VALIDATION_ERROR,
          'Analysis query is too long (maximum 500 characters)',
          { field: 'query', maxLength: 500 },
          false
        );
      }
    }

    // Validate workspace fields if provided
    if (params.workspace_name && params.workspace_name.length > 100) {
      throw createError(
        AiAnalysisWebhookErrorType.VALIDATION_ERROR,
        'Workspace name is too long (maximum 100 characters)',
        { field: 'workspace_name', maxLength: 100 },
        false
      );
    }

    // Validate workspace_id format if provided
    if (params.workspace_id && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(params.workspace_id)) {
      throw createError(
        AiAnalysisWebhookErrorType.VALIDATION_ERROR,
        'Invalid workspace ID format',
        { field: 'workspace_id' },
        false
      );
    }
  }, [createError]);

  /**
   * Main webhook trigger function with enhanced AI analysis support
   */
  /**
   * Core AI analysis execution function (without usage checks)
   * This is the actual webhook logic that will be wrapped with usage middleware
   */
  const executeAiAnalysisWebhook = useCallback(async (params: AiAnalysisWebhookTriggerParams): Promise<AiAnalysisWebhookTriggerResponse> => {
    // Configuration check
    const webhookUrl = import.meta.env.VITE_AI_ANALYSIS_WEBHOOK;
    if (!webhookUrl) {
      const configError = createError(
        AiAnalysisWebhookErrorType.CONFIGURATION_ERROR,
        'AI Analysis webhook URL not configured',
        undefined,
        false
      );
      throw configError;
    }

    // Fetch workspace data if this is workspace analysis
    let workspaceData;
    if (params.analysis_type === 'workspace_analysis' && params.workspace_id) {
      try {
        workspaceData = await fetchWorkspaceData(params.workspace_id, userId);
        debugLog(userEmail, '[useAiAnalysisWebhook] Workspace data prepared for analysis:', {
          total_keywords: workspaceData.total_keywords,
          data_types: workspaceData.data_types
        });
      } catch (fetchError) {
        throw fetchError;
      }
    }

    // 🛡️ CRITICAL SECURITY: Validate workspace ownership before processing
    const workspaceValidation = await validateWorkspaceOwnership(userId, params.workspace_id!);
    if (!workspaceValidation.isValid) {
      const securityError = createError(
        AiAnalysisWebhookErrorType.SECURITY_ERROR,
        `Workspace access denied: ${workspaceValidation.violations.join(', ')}`,
        { workspaceId: params.workspace_id, violations: workspaceValidation.violations },
        true
      );
      throw securityError;
    }

    // 🔒 SECURE PAYLOAD: Create isolated workspace payload with validation tokens
    const securePayload = createSecureAIWebhookPayload(
      userId,
      params.workspace_id!,
      params.workspace_name!,
      workspaceData?.keywords || []
    );

    // Prepare enhanced payload matching N8N expected format with security enhancements
    const payload: AiAnalysisWebhookPayload = {
      ...securePayload,
      analysis_type: 'full_analysis', // Match the expected analysis type
      data_filter: {
        data_types: workspaceData?.data_types || [],
        date_range: workspaceData ? {
          start: workspaceData.date_range.oldest_keyword,
          end: workspaceData.date_range.newest_keyword
        } : undefined
      },
      include_data: params.include_data || true,
      use_aggregated: false,
      timestamp: new Date().toISOString(),
      metadata: {
        source: 'keyword_os_frontend',
        version: '2.0.0-secure', // Updated version with security enhancements
        user_agent: navigator.userAgent,
        workspace_context: {
          workspace_id: params.workspace_id!,
          workspace_name: params.workspace_name!,
          total_keywords: workspaceData?.total_keywords || 0,
          data_types: workspaceData?.data_types || [],
          date_range: {
            oldest_keyword: workspaceData?.date_range.oldest_keyword || new Date().toISOString(),
            newest_keyword: workspaceData?.date_range.newest_keyword || new Date().toISOString()
          }
        },
        // 🛡️ SECURITY METADATA for contamination prevention
        security_context: {
          isolation_level: 'strict',
          workspace_validated: true,
          user_validated: true,
          session_id: securePayload.isolation.session_id,
          validation_token: securePayload.isolation.validation_token,
          processing_queue: securePayload.isolation.processing_queue
        }
      }
    };

    debugLog(userEmail, '[useAiAnalysisWebhook] 🛡️ SECURE PAYLOAD created with isolation context:', {
      session_id: securePayload.isolation.session_id,
      workspace_validated: workspaceValidation.isValid,
      security_level: workspaceValidation.securityLevel
    });


    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      debugLog(userEmail, '[useAiAnalysisWebhook] Triggering AI analysis webhook with payload:', {
        ...payload,
        keyword_data: `${payload.keyword_data.length} records` // Don't log all data
      });

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: abortControllerRef.current.signal,
        // 120 second timeout for AI analysis
      });

      // Set up timeout - 120 seconds for AI analysis
      const timeoutId = setTimeout(() => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      }, 120000);

      let responseData: AiAnalysisWebhookTriggerResponse;

      if (!response.ok) {
        clearTimeout(timeoutId);
        
        let errorDetails;
        try {
          errorDetails = await response.text();
        } catch {
          errorDetails = 'Unknown server error';
        }

        const serverError = createError(
          response.status >= 500 ? AiAnalysisWebhookErrorType.SERVER_ERROR : AiAnalysisWebhookErrorType.NETWORK_ERROR,
          `AI Analysis webhook failed (${response.status}): ${errorDetails}`,
          { status: response.status, details: errorDetails },
          response.status >= 500 || response.status === 429 // Retry on server errors and rate limits
        );
        
        throw serverError;
      }

      clearTimeout(timeoutId);

      // Handle response parsing
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        const textResponse = await response.text();
        responseData = { 
          success: true, 
          message: textResponse || 'AI Analysis processing started successfully',
        };
      }

      debugLog(userEmail, '[useAiAnalysisWebhook] AI Analysis webhook response:', responseData);

      // Store execution tracking if we have execution details
      if (responseData.execution_id && responseData.workflow_id) {
        setLastExecutionId(responseData.execution_id);
        
        // Store workflow execution in background
        storeWorkflowExecution(
          responseData.execution_id,
          responseData.workflow_id,
          params
        ).catch(err => {
          debugWarn(userEmail, '[useAiAnalysisWebhook] Failed to store workflow execution:', err);
        });
      }

      return responseData;

    } catch (err: any) {
      debugError(userEmail, '[useAiAnalysisWebhook] AI Analysis webhook error:', err);

      if (err.name === 'AbortError') {
        const timeoutError = createError(
          AiAnalysisWebhookErrorType.TIMEOUT_ERROR,
          'AI Analysis request timed out - your analysis is still processing',
          undefined,
          true
        );
        throw timeoutError;
      }

      if (err instanceof TypeError && err.message.includes('fetch')) {
        const networkError = createError(
          AiAnalysisWebhookErrorType.NETWORK_ERROR,
          'Network connection failed',
          { originalError: err.message },
          true
        );
        throw networkError;
      }

      // If it's already an AiAnalysisWebhookError, re-throw it
      if (err.type && Object.values(AiAnalysisWebhookErrorType).includes(err.type)) {
        throw err;
      }

      // Generic error fallback
      const genericError = createError(
        AiAnalysisWebhookErrorType.WORKFLOW_CREATION_ERROR,
        err.message || 'An unexpected error occurred during AI analysis',
        { originalError: err },
        true
      );
      throw genericError;

    } finally {
      abortControllerRef.current = null;
    }
  }, [userId, validateParams, createError, storeWorkflowExecution, fetchWorkspaceData]);

  /**
   * Main AI analysis trigger function with usage checks and enhanced error handling
   */
  const triggerWebhook = useCallback(async (params: AiAnalysisWebhookTriggerParams): Promise<AiAnalysisWebhookTriggerResponse> => {
    // Authentication check
    if (!isAuthenticated || !userId) {
      const authError = createError(
        AiAnalysisWebhookErrorType.AUTHENTICATION_ERROR,
        'User authentication required',
        undefined,
        false
      );
      setError(authError);
      throw authError;
    }

    // Parameter validation
    try {
      validateParams(params);
    } catch (validationError) {
      setError(validationError as AiAnalysisWebhookError);
      throw validationError;
    }

    // Store parameters for potential retry
    lastParamsRef.current = params;

    setIsLoading(true);
    setError(null);

    try {
      // Use usage middleware to check and consume usage before executing AI analysis
      const { result, usageType } = await withAIAnalysisUsageCheck(
        userId,
        () => executeAiAnalysisWebhook(params),
        params.workspace_id,
        params.analysis_type || 'workspace_analysis'
      );

      debugLog(userEmail, `[useAiAnalysisWebhook] AI analysis completed using ${usageType} usage`);
      
      return result;

    } catch (error: any) {
      // Handle usage-related errors with user-friendly messages
      const usageErrorInfo = handleUsageError(error);
      
      if (usageErrorInfo.isUsageError) {
        const usageError = createError(
          AiAnalysisWebhookErrorType.VALIDATION_ERROR,
          usageErrorInfo.userMessage,
          { usageError: true, shouldShowPurchase: usageErrorInfo.shouldShowPurchase },
          false
        );
        setError(usageError);
        throw usageError;
      }

      // Handle other errors (re-throw AI analysis errors as-is)
      if (error.type && Object.values(AiAnalysisWebhookErrorType).includes(error.type)) {
        setError(error);
        throw error;
      }

      // Generic error fallback
      const genericError = createError(
        AiAnalysisWebhookErrorType.WORKFLOW_CREATION_ERROR,
        error?.message || 'An unexpected error occurred during AI analysis',
        { originalError: error },
        true
      );
      setError(genericError);
      throw genericError;

    } finally {
      setIsLoading(false);
    }
  }, [userId, isAuthenticated, validateParams, executeAiAnalysisWebhook, userEmail, createError]);

  /**
   * Retry the last failed trigger
   */
  const retryLastTrigger = useCallback(async (): Promise<void> => {
    if (!lastParamsRef.current) {
      throw new Error('No previous trigger to retry');
    }

    if (!error?.retryable) {
      throw new Error('Last error is not retryable');
    }

    await triggerWebhook(lastParamsRef.current);
  }, [error, triggerWebhook]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    triggerWebhook,
    isLoading,
    error,
    lastExecutionId,
    retryLastTrigger,
    clearError,
    getErrorMessage,
  };
}